import React, { useEffect, useState, useContext } from "react";
import "../styles.scss";
import "./colaborador-modal.scss";
import { Formik } from "formik";
import { BsTrash } from "react-icons/bs";
import Lottie from "react-lottie";
import MainContext from "../../../Contexts/MainContext";
import {
    deleteColaboradorById,
    getAllExpediente,
    getAllFuncoes,
    getExpedienteById,
    getFuncaoById,
    insertNewLog,
    updateColaboradorById,
    resetUserPassword,
} from "../../../Services/ApiCalls";
import { showToast, validateEmail } from "../../../Functions";
import { customConfirmAlert as confirmAlert } from '../../../Functions';

const LOADING = require("../../../Assets/animations/loading.json");
const SUCCESS = require("../../../Assets/animations/success.json");
const ERROR = require("../../../Assets/animations/error.json");

interface CustomCost {
    id: string;
    label: string;
    value: string;
    tipo: "total" | "diario";
    incluirCusto: boolean;
}

interface CostField {
    value: string;
    tipo: "total" | "diario";
    incluirCusto: boolean;
}

interface SelectedRowPrColaborador {
    data: any;
}

const InfoIcon = ({ tooltip }: { tooltip: string }) => (
    <span className="info-icon" data-tooltip={tooltip}>i</span>
);

const SelectedColaborador: React.FC<SelectedRowPrColaborador> = ({ data }) => {
    const {
        token,
        setOpenMoreInfo,
        sethasCloseEditModal,
        removehasCloseEditModal,
        currentLoggedUserId,
        setNotificationCount,
        notificationCount,
    } = useContext(MainContext);

    const { id, nome, email, funcaoId, expedienteId, ativo } = data;
    const [cpf, setCpf] = useState<string>(data.cpf || "");
    const [dataNascimento, setDataNascimento] = useState<string>(data.data_nascimento || "");

    const [activeTab, setActiveTab] = useState<"dados" | "custos">("dados");
    const [perfil, setPerfil] = useState<"ADMIN" | "COLABORADOR">(data.perfil || "COLABORADOR");

    const [salario, setSalario] = useState<CostField>(
        data.custos?.salario || { value: "", tipo: "total", incluirCusto: true }
    );
    const [valeTransporte, setValeTransporte] = useState<CostField>(
        data.custos?.valeTransporte || { value: "", tipo: "total", incluirCusto: true }
    );
    const [valeAlimentacao, setValeAlimentacao] = useState<CostField>(
        data.custos?.valeAlimentacao || { value: "", tipo: "total", incluirCusto: true }
    );
    const [customCosts, setCustomCosts] = useState<CustomCost[]>(
        data.custos?.customCosts || []
    );

    const [loadingFuncao, setIsLoadingFuncao] = useState(false);
    const [funcaoName, setFuncaoName] = useState("");
    const [isLoadingAllFunçoes, setIsLoadingAllFunçoes] = useState(false);
    const [allFuncoes, setAllFuncoes] = useState<any[]>([]);
    const [selectedFuncaoId, setSelectedFuncaoId] = useState(funcaoId);
    const [selectedExpedienteId, setSelectedExpedienteId] = useState(expedienteId);

    const [isSubmiting, setIsSubmiting] = useState(false);
    const [updateSuccess, setupdateSuccess] = useState(false);
    const [updateError, setUpdateError] = useState(false);
    const [isAtivo, setIsAtivo] = useState(
        typeof ativo === "boolean" ? ativo : ativo?.props?.values === "true"
    );

    const [loadingExpediente, setIsLoadingExpediente] = useState(false);
    const [expedienteName, setExpedienteName] = useState("");
    const [isLoadingAllExpedientes, setIsLoadingAllExpedientes] = useState(false);
    const [allExpedientes, setAllExpedientes] = useState<any[]>([]);

    const [newPassword, setNewPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);

    const formValues = { id, nome, email, funcaoId, expedienteId, ativo, cpf };

    useEffect(() => {
        getFuncaoName();
        getTodasFuncoes();
        getExpedienteName();
        getTodosExpediente();
    }, []);

    const askIfDelete = () => {
        confirmAlert({
            title: "Atenção: ação irreversível",
            message: `Tem certeza que deseja excluir ${nome}? O histórico de pontos pode ser afetado.`,
            buttons: [
                {
                    label: "Sim, excluir",
                    onClick: async () => {
                        setIsSubmiting(true);
                        const responseRemove = await deleteColaboradorById(token, id);
                        if (responseRemove?.status === 200) {
                            setupdateSuccess(true);
                            insertNewLog(currentLoggedUserId, `Usuário #${id} - ${nome} removido`);
                            setNotificationCount(notificationCount + 1);
                            window.setTimeout(() => {
                                showToast("SUCCESS", `Colaborador ${nome} excluído com sucesso!`, {});
                                sethasCloseEditModal(true);
                                removehasCloseEditModal("closedModal");
                                setOpenMoreInfo(false);
                            }, 2000);
                        } else {
                            setUpdateError(true);
                            showToast("ERROR", "Algo deu errado 😣", {});
                            setIsSubmiting(false);
                        }
                    },
                },
                { label: "Cancelar", onClick: () => {} },
            ],
        });
    };

    const getFuncaoName = async () => {
        setIsLoadingFuncao(true);
        const resp = await getFuncaoById(token, funcaoId);
        if (resp?.status === 200) setFuncaoName(resp.data.nome);
        setIsLoadingFuncao(false);
    };

    const getTodasFuncoes = async () => {
        setIsLoadingAllFunçoes(true);
        const resp = await getAllFuncoes(token);
        // Include ALL functions — controlled select handles the current value
        if (resp?.status === 200) setAllFuncoes(resp.data);
        setIsLoadingAllFunçoes(false);
    };

    const getExpedienteName = async () => {
        setIsLoadingExpediente(true);
        const resp = await getExpedienteById(token, expedienteId);
        if (resp?.status === 200) setExpedienteName(resp.data.nome);
        setIsLoadingExpediente(false);
    };

    const getTodosExpediente = async () => {
        setIsLoadingAllExpedientes(true);
        const resp = await getAllExpediente(token);
        // Include ALL expedientes — controlled select handles the current value
        if (resp?.status === 200) setAllExpedientes(resp.data);
        setIsLoadingAllExpedientes(false);
    };

    const handleResetPassword = () => {
        confirmAlert({
            title: "Atenção: alteração de senha",
            message: `Tem certeza que deseja alterar a senha de ${nome}? A nova senha terá efeito imediato e o usuário usará a nova senha para acessar o painel.`,
            buttons: [
                {
                    label: "Sim, alterar senha",
                    onClick: async () => {
                        setIsResettingPassword(true);
                        const response = await resetUserPassword(token, id, newPassword);
                        if (response?.status === 200) {
                            showToast("SUCCESS", `Senha de ${nome} alterada com sucesso!`, {});
                            setNewPassword("");
                            setShowNewPassword(false);
                        } else {
                            showToast("ERROR", response?.response?.data?.message || "Erro ao alterar a senha 😣", {});
                        }
                        setIsResettingPassword(false);
                    },
                },
                { label: "Cancelar", onClick: () => {} },
            ],
        });
    };

    const addCustomCost = () => {
        setCustomCosts([...customCosts, {
            id: `custom-${Date.now()}`,
            label: "", value: "", tipo: "total", incluirCusto: true,
        }]);
    };

    const removeCustomCost = (costId: string) => {
        setCustomCosts(customCosts.filter((c) => c.id !== costId));
    };

    const updateCustomCost = (costId: string, field: keyof CustomCost, val: any) => {
        setCustomCosts(customCosts.map((c) => (c.id === costId ? { ...c, [field]: val } : c)));
    };

    // Compact cost row component
    const CostRow = ({
        label, tooltip, field, setField, showTipo = false,
    }: {
        label: string; tooltip: string; field: CostField;
        setField: (v: CostField) => void; showTipo?: boolean;
    }) => (
        <div className="cost-row">
            <div className="cost-row__label">
                <span>{label}</span>
                <InfoIcon tooltip={tooltip} />
            </div>
            <div className="cost-row__controls">
                <label className="toggle-switch small-toggle">
                    <input
                        type="checkbox"
                        checked={field.incluirCusto}
                        onChange={(e) => setField({ ...field, incluirCusto: e.target.checked })}
                    />
                    <span className="slider"></span>
                </label>
                <div className="cost-row__value">
                    <span className="cost-prefix">R$</span>
                    <input
                        type="number" step="0.01" min="0" placeholder="0,00"
                        value={field.value}
                        onChange={(e) => setField({ ...field, value: e.target.value })}
                        className="cost-input"
                    />
                </div>
                {showTipo && (
                    <div className="cost-tipo-group">
                        <button type="button"
                            className={`tipo-btn ${field.tipo === "total" ? "active" : ""}`}
                            onClick={() => setField({ ...field, tipo: "total" })}>
                            Total
                        </button>
                        <button type="button"
                            className={`tipo-btn ${field.tipo === "diario" ? "active" : ""}`}
                            onClick={() => setField({ ...field, tipo: "diario" })}>
                            Diário
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="selected__wrapper colab-modal-wrapper">
            {!isSubmiting ? (
                <Formik
                    initialValues={formValues}
                    onSubmit={async (values) => {
                        const { id, nome, email } = values;
                        setIsSubmiting(true);
                        const payload = {
                            id, nome, email,
                            cpf,
                            data_nascimento: dataNascimento,
                            funcaoId: Number(selectedFuncaoId),
                            expedienteId: Number(selectedExpedienteId),
                            ativo: isAtivo, perfil,
                            custos: { salario, valeTransporte, valeAlimentacao, customCosts },
                        };
                        const responseSubmit = await updateColaboradorById(token, id, payload);
                        if (responseSubmit?.status === 200) {
                            setupdateSuccess(true);
                            insertNewLog(currentLoggedUserId, `Alterações no usuário #${id} - ${nome}`);
                            setNotificationCount(notificationCount + 1);
                            showToast("SUCCESS", "Alterações salvas com sucesso!", {});
                            window.setTimeout(() => {
                                sethasCloseEditModal(true);
                                removehasCloseEditModal("closedModal");
                                setOpenMoreInfo(false);
                            }, 2000);
                        } else {
                            setUpdateError(true);
                            showToast("ERROR", "Não foi possível salvar as alterações", {});
                            setIsSubmiting(false);
                        }
                    }}
                    validate={(values) => {
                        const errors: any = {};
                        if (!values.nome) errors.nome = "Insira um nome";
                        if (!values.email || !validateEmail(values.email)) errors.email = "E-mail inválido";
                        return errors;
                    }}
                >
                    {({ values, handleChange, handleSubmit, errors }) => (
                        <form onSubmit={handleSubmit} className="colab-form">

                            {/* ── HEADER ── */}
                            <div className="colab-modal-header">
                                <div className="colab-avatar">
                                    {nome?.charAt(0)?.toUpperCase() || "?"}
                                </div>
                                <div className="colab-header-info">
                                    <h2>{nome}</h2>
                                </div>
                                <div className="colab-header-right">
                                    {/* Toggle removido do header */}
                                </div>
                            </div>

                            {/* ── PERFIL DE ACESSO ── */}
                            <div className="profile-selector-section">
                                <label className="section-label">Perfil de Acesso</label>
                                <div className="profile-pills">
                                    <button type="button"
                                        className={`profile-pill ${perfil === "ADMIN" ? "selected admin" : ""}`}
                                        onClick={() => setPerfil("ADMIN")}>
                                        Administrador
                                    </button>
                                    <button type="button"
                                        className={`profile-pill ${perfil === "COLABORADOR" ? "selected colab" : ""}`}
                                        onClick={() => setPerfil("COLABORADOR")}>
                                        Colaborador
                                    </button>
                                </div>
                                <p className="profile-desc">
                                    {perfil === "ADMIN"
                                        ? "Acesso completo: gerencia usuários, pontos, expedientes e relatórios."
                                        : "Acesso restrito: visualiza e registra seus próprios pontos."}
                                </p>
                            </div>

                            {/* ── TABS ── */}
                            <div className="colab-tabs">
                                <button type="button"
                                    className={`colab-tab ${activeTab === "dados" ? "active" : ""}`}
                                    onClick={() => setActiveTab("dados")}>
                                    Dados Cadastrais
                                </button>
                                <button type="button"
                                    className={`colab-tab ${activeTab === "custos" ? "active" : ""}`}
                                    onClick={() => setActiveTab("custos")}>
                                    Custos
                                </button>
                            </div>

                            {/* ── TAB DADOS CADASTRAIS ── */}
                            {activeTab === "dados" && (
                                <div className="tab-content">
                                    {/* Row 1: Nome (2/3) | CPF (1/3) */}
                                    <div className="form-grid-row">
                                        <div className="form-cell grow-2">
                                            <label>Nome completo</label>
                                            <input type="text" value={values.nome}
                                                className={errors.nome ? "hasError" : ""}
                                                onChange={handleChange("nome")}
                                                placeholder="Nome do colaborador" />
                                            {errors.nome && <div className="form__error"><p>{errors.nome}</p></div>}
                                        </div>
                                        <div className="form-cell grow-1">
                                            <label>CPF</label>
                                            <input
                                                type="text"
                                                value={cpf}
                                                onChange={(e) => setCpf(e.target.value)}
                                                placeholder="000.000.000-00"
                                                maxLength={14}
                                            />
                                        </div>
                                        <div className="form-cell grow-1">
                                            <label>Nascimento</label>
                                            <input
                                                type="date"
                                                value={dataNascimento}
                                                onChange={(e) => setDataNascimento(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Row 2: E-mail (2/3) | Status (1/3) */}
                                    <div className="form-grid-row">
                                        <div className="form-cell grow-2">
                                            <label>E-mail</label>
                                            <input type="email" value={values.email}
                                                className={errors.email ? "hasError" : ""}
                                                onChange={handleChange("email")}
                                                placeholder="email@empresa.com" />
                                            {errors.email && <div className="form__error"><p>{errors.email}</p></div>}
                                        </div>
                                        <div className="form-cell grow-1 cell-center">
                                            <label>Status</label>
                                            <div className="status-cell-content">
                                                <label className="toggle-switch">
                                                    <input type="checkbox" checked={isAtivo} onChange={() => setIsAtivo(!isAtivo)} />
                                                    <span className="slider"></span>
                                                </label>
                                                <span className={`status-badge ${isAtivo ? "active" : "inactive"}`}>
                                                    {isAtivo ? "Ativo" : "Inativo"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 3: Função (50%) | Expediente (50%) */}
                                    <div className="form-grid-row">
                                        <div className="form-cell grow-1">
                                            <label>Função</label>
                                            <select
                                                value={selectedFuncaoId}
                                                onChange={(e) => setSelectedFuncaoId(e.target.value)}
                                            >
                                                {isLoadingAllFunçoes
                                                    ? <option value={funcaoId}>Carregando...</option>
                                                    : allFuncoes.map((f) => (
                                                        <option key={f.id} value={f.id}>{f.nome}</option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                        <div className="form-cell grow-1">
                                            <label>Expediente</label>
                                            <select
                                                value={selectedExpedienteId}
                                                onChange={(e) => setSelectedExpedienteId(e.target.value)}
                                            >
                                                {isLoadingAllExpedientes
                                                    ? <option value={expedienteId}>Carregando...</option>
                                                    : allExpedientes.map((e) => (
                                                        <option key={e.id} value={e.id}>{e.nome}</option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>

                                    {/* Row 4: Redefinir Senha */}
                                    <div className="form-grid-row password-reset-row" style={{ marginTop: '15px' }}>
                                        <div className="form-cell grow-1" style={{ padding: '15px', background: '#f5f7fa', borderRadius: '8px', border: '1px solid #e1e4e8' }}>
                                            <label style={{ color: '#2d3748', fontWeight: 600 }}>Redefinir Senha do Colaborador</label>
                                            <div className="password-reset-input-group" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                <div className="password-input-wrapper" style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
                                                    <input 
                                                        type={showNewPassword ? "text" : "password"}
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        placeholder="Nova senha (mín. 6 chars)"
                                                        style={{ width: '100%', paddingRight: '40px', margin: 0 }}
                                                    />
                                                    <span 
                                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.6 }}
                                                    >
                                                        {showNewPassword ? "👁️‍🗨️" : "👁️"}
                                                    </span>
                                                </div>
                                                <button 
                                                    type="button" 
                                                    className="button__sucess"
                                                    disabled={isResettingPassword || newPassword.length < 6}
                                                    onClick={handleResetPassword}
                                                    style={{ padding: '0 20px', margin: 0, height: '42px' }}
                                                >
                                                    {isResettingPassword ? "Salvando..." : "Salvar Nova Senha"}
                                                </button>
                                            </div>
                                            <p className="profile-desc" style={{ marginTop: '10px', fontSize: '12px', color: '#718096' }}>
                                                A senha atual não é exibida por motivos de segurança. Ao redefinir, a nova senha terá efeito imediato.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── TAB CUSTOS ── */}
                            {activeTab === "custos" && (
                                <div className="tab-content costs-tab">
                                    {/* Header da tabela de custos */}
                                    <div className="costs-table-header">
                                        <span>Item</span>
                                        <span>Base salarial</span>
                                        <span>Valor (R$)</span>
                                        <span>Tipo</span>
                                    </div>

                                    <CostRow
                                        label="Salário"
                                        tooltip="Valor bruto fixo mensal registrado em contrato, sem descontos ou adicionais variáveis."
                                        field={salario} setField={setSalario}
                                    />
                                    <CostRow
                                        label="Vale Transporte"
                                        tooltip={"'Total': valor fixo mensal.\n'Diário': pago por dia de presença efetiva registrada no ponto."}
                                        field={valeTransporte} setField={setValeTransporte} showTipo
                                    />
                                    <CostRow
                                        label="Vale Alimentação"
                                        tooltip={"'Total': valor fixo mensal.\n'Diário': pago por dia de presença efetiva registrada no ponto."}
                                        field={valeAlimentacao} setField={setValeAlimentacao} showTipo
                                    />

                                    {/* Custos dinâmicos */}
                                    {customCosts.map((cost) => (
                                        <div key={cost.id} className="cost-row custom-cost-row">
                                            <div className="cost-row__label">
                                                <input
                                                    type="text"
                                                    className="custom-cost-label-input"
                                                    placeholder="Nome do custo"
                                                    value={cost.label}
                                                    onChange={(e) => updateCustomCost(cost.id, "label", e.target.value)}
                                                />
                                            </div>
                                            <div className="cost-row__controls">
                                                <label className="toggle-switch small-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={cost.incluirCusto}
                                                        onChange={(e) => updateCustomCost(cost.id, "incluirCusto", e.target.checked)}
                                                    />
                                                    <span className="slider"></span>
                                                </label>
                                                <div className="cost-row__value">
                                                    <span className="cost-prefix">R$</span>
                                                    <input
                                                        type="number" step="0.01" min="0" placeholder="0,00"
                                                        value={cost.value}
                                                        onChange={(e) => updateCustomCost(cost.id, "value", e.target.value)}
                                                        className="cost-input"
                                                    />
                                                </div>
                                                <div className="cost-tipo-group">
                                                    <button type="button"
                                                        className={`tipo-btn ${cost.tipo === "total" ? "active" : ""}`}
                                                        onClick={() => updateCustomCost(cost.id, "tipo", "total")}>
                                                        Total
                                                    </button>
                                                    <button type="button"
                                                        className={`tipo-btn ${cost.tipo === "diario" ? "active" : ""}`}
                                                        onClick={() => updateCustomCost(cost.id, "tipo", "diario")}>
                                                        Diário
                                                    </button>
                                                    <button type="button"
                                                        className="tipo-btn remove-icon-btn"
                                                        title="Remover custo"
                                                        onClick={() => removeCustomCost(cost.id)}>
                                                        <BsTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <button type="button" className="btn-add-cost" onClick={addCustomCost}>
                                        + Adicionar novo custo
                                    </button>
                                </div>
                            )}

                            {/* ── FOOTER ── */}
                            <div className="colab-modal-footer">
                                <button type="button" className="btn-delete-ghost" onClick={askIfDelete}>
                                    <BsTrash /> Excluir colaborador
                                </button>
                                <button type="submit" className="btn-save-colab"
                                    disabled={isLoadingAllExpedientes || isLoadingAllFunçoes || !!errors.nome || !!errors.email}>
                                    Salvar alterações
                                </button>
                            </div>
                        </form>
                    )}
                </Formik>
            ) : (
                <div className="colab-submitting">
                    <Lottie
                        options={{
                            loop: !updateSuccess,
                            animationData: !updateSuccess && !updateError ? LOADING : updateSuccess ? SUCCESS : ERROR,
                        }}
                        height={120} width={120}
                    />
                    <p>{updateSuccess ? "Salvo com sucesso!" : updateError ? "Ops, algo deu errado" : "Salvando..."}</p>
                </div>
            )}
        </div>
    );
};

export default SelectedColaborador;
